import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Package, CheckCircle, Clock, Truck, 
  Eye, Download, Calendar, MapPin, User, Phone, 
  ArrowUpRight, ArrowDownRight, RefreshCw, X,
  ChevronLeft, ChevronRight, MoreHorizontal
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSeller } from '../../context/SellerContext';

const Orders = () => {
  const navigate = useNavigate();
  const { orders, loading, updateOrderStatus, refreshData } = useSeller();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const handleUpdateStatus = async (orderId, newStatus) => {
    const res = await updateOrderStatus(orderId, newStatus);
    if (res && res.success) {
      setShowDetailModal(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Delivered': 
        return { 
          bg: '#d1fae5', 
          color: '#059669', 
          label: 'Delivered', 
          icon: CheckCircle,
          borderColor: '#10b981'
        };
      case 'Shipped': 
        return { 
          bg: '#dbeafe', 
          color: '#0284c7', 
          label: 'Shipped', 
          icon: Truck,
          borderColor: '#3b82f6'
        };
      case 'Processing': 
        return { 
          bg: '#fed7aa', 
          color: '#d97706', 
          label: 'Processing', 
          icon: Clock,
          borderColor: '#f59e0b'
        };
      case 'Cancelled': 
        return { 
          bg: '#fee2e2', 
          color: '#dc2626', 
          label: 'Cancelled', 
          icon: Package,
          borderColor: '#ef4444'
        };
      default: 
        return { 
          bg: '#f1f5f9', 
          color: '#64748b', 
          label: status, 
          icon: Package,
          borderColor: '#cbd5e1'
        };
    }
  };

  const getStats = () => {
    const totalOrders = orders.length;
    const completedOrders = orders.filter(o => o.status === 'Delivered').length;
    const processingOrders = orders.filter(o => o.status === 'Processing').length;
    const totalRevenue = orders.reduce((sum, o) => sum + o.totalAmount, 0);
    
    return { totalOrders, completedOrders, processingOrders, totalRevenue };
  };

  const stats = getStats();

  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    const orderIdMatch = order._id.toLowerCase().includes(searchLower);
    const customerMatch = order.buyer?.username?.toLowerCase().includes(searchLower) || 
                         order.shippingAddress?.firstName?.toLowerCase().includes(searchLower);
    const productMatch = order.orderItems.some(item => item.name.toLowerCase().includes(searchLower));
    const statusMatch = selectedStatus === 'all' || order.status === selectedStatus;
    
    return (orderIdMatch || customerMatch || productMatch) && statusMatch;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card-mini">
      <div className="stat-icon-mini" style={{ backgroundColor: bgColor }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <p className="stat-title-mini">{title}</p>
        <h4 className="stat-value-mini">{typeof value === 'number' ? value.toLocaleString() : value}</h4>
      </div>
    </div>
  );

  return (
    <div className="orders-page">
      {/* Header */}
      <div className="orders-header">
        <div className="header-left">
          <h1 className="page-title">Orders</h1>
          <p className="page-subtitle">Manage and track all customer orders</p>
        </div>
        <button className="btn-refresh" onClick={refreshData}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-mini-grid">
        <StatCard title="Total Orders" value={stats.totalOrders} icon={Package} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Completed" value={stats.completedOrders} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Processing" value={stats.processingOrders} icon={Clock} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Revenue" value={`₹${stats.totalRevenue.toLocaleString()}`} icon={ArrowUpRight} color="#8b5cf6" bgColor="#e9d5ff" />
      </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            className="search-input-modern" 
            placeholder="Search by Order ID, Customer, or Product..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="status-filters">
          <button 
            className={`status-filter-btn ${selectedStatus === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('all')}
          >
            All
          </button>
          <button 
            className={`status-filter-btn ${selectedStatus === 'Processing' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('Processing')}
          >
            <Clock size={12} /> Processing
          </button>
          <button 
            className={`status-filter-btn ${selectedStatus === 'Shipped' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('Shipped')}
          >
            <Truck size={12} /> Shipped
          </button>
          <button 
            className={`status-filter-btn ${selectedStatus === 'Delivered' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('Delivered')}
          >
            <CheckCircle size={12} /> Delivered
          </button>
          <button 
            className={`status-filter-btn ${selectedStatus === 'Cancelled' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('Cancelled')}
          >
            <Package size={12} /> Cancelled
          </button>
        </div>
        
        <button className="btn-export-data">
          <Download size={16} />
          Export
        </button>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="empty-container">
          <div className="empty-icon-wrapper">
            <Package size={64} />
          </div>
          <h3>No orders found</h3>
          <p>You have no orders matching your search criteria.</p>
        </div>
      ) : (
        <>
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Products</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentOrders.map((order) => {
                  const statusConfig = getStatusConfig(order.status);
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr key={order._id}>
                      <td>
                        <div className="order-id-cell">
                          <span className="order-id">#{order._id.slice(-8).toUpperCase()}</span>
                        </div>
                      </td>
                      <td>
                        <div className="customer-info">
                          <span className="customer-name">
                            {order.shippingAddress?.firstName} {order.shippingAddress?.lastName}
                          </span>
                          <div className="customer-contact">
                            <Phone size={10} />
                            <span>{order.shippingAddress?.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="products-cell">
                        <div className="products-preview">
                          {order.orderItems.slice(0, 2).map((item, idx) => (
                            <div key={idx} className="product-item-preview">
                              <img 
                                src={item.product?.image || '/assets/images/thumbs/plant-placeholder.png'} 
                                alt={item.name}
                                onError={(e) => { e.target.src = '/assets/images/thumbs/plant-placeholder.png'; }}
                              />
                              <span className="product-name-preview">
                                {item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name}
                                <small>x{item.quantity}</small>
                              </span>
                            </div>
                          ))}
                          {order.orderItems.length > 2 && (
                            <div className="more-products">+{order.orderItems.length - 2} more</div>
                          )}
                        </div>
                       </td>
                      <td>
                        <div className="date-info">
                          <Calendar size={12} />
                          <span>{new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                      </td>
                      <td>
                        <div className="amount-cell">₹{order.totalAmount.toLocaleString()}</div>
                      </td>
                      <td>
                        <div className="status-indicator" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                          <StatusIcon size={12} />
                          <span>{statusConfig.label}</span>
                        </div>
                      </td>
                      <td className="text-right">
                        <div className="action-buttons">
                          <button 
                            className="action-view" 
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowDetailModal(true);
                            }}
                          >
                            <Eye size={16} />
                            View
                          </button>
                          {order.status === 'Processing' && (
                            <button 
                              className="action-ship" 
                              onClick={() => handleUpdateStatus(order._id, 'Shipped')}
                            >
                              <Truck size={16} />
                              Ship
                            </button>
                          )}
                          {order.status === 'Shipped' && (
                            <button 
                              className="action-deliver" 
                              onClick={() => handleUpdateStatus(order._id, 'Delivered')}
                            >
                              <CheckCircle size={16} />
                              Deliver
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-modern">
              <button 
                className="page-btn-modern" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <div className="page-numbers-modern">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`page-number-modern ${currentPage === i + 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button 
                className="page-btn-modern" 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Order Details</h3>
                <p className="modal-subtitle">Order #{selectedOrder._id.slice(-8).toUpperCase()}</p>
              </div>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              {/* Status Update */}
              <div className="modal-section">
                <h4>Update Status</h4>
                <div className="status-update-buttons">
                  <button 
                    className={`status-update-btn ${selectedOrder.status === 'Processing' ? 'active' : ''}`}
                    onClick={() => handleUpdateStatus(selectedOrder._id, 'Processing')}
                  >
                    <Clock size={14} /> Processing
                  </button>
                  <button 
                    className={`status-update-btn ${selectedOrder.status === 'Shipped' ? 'active' : ''}`}
                    onClick={() => handleUpdateStatus(selectedOrder._id, 'Shipped')}
                  >
                    <Truck size={14} /> Shipped
                  </button>
                  <button 
                    className={`status-update-btn ${selectedOrder.status === 'Delivered' ? 'active' : ''}`}
                    onClick={() => handleUpdateStatus(selectedOrder._id, 'Delivered')}
                  >
                    <CheckCircle size={14} /> Delivered
                  </button>
                </div>
              </div>

              {/* Order Items */}
              <div className="modal-section">
                <h4>Order Items</h4>
                <div className="order-items-list">
                  {selectedOrder.orderItems.map((item, idx) => (
                    <div key={idx} className="order-item-detail">
                      <img src={item.product?.image || '/assets/images/thumbs/plant-placeholder.png'} alt={item.name} />
                      <div className="item-info">
                        <div className="item-name">{item.name}</div>
                        <div className="item-price">₹{item.price} x {item.quantity}</div>
                      </div>
                      <div className="item-subtotal">₹{(item.price * item.quantity).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer Info */}
              <div className="modal-section">
                <h4>Customer Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <User size={14} />
                    <span>{selectedOrder.shippingAddress?.firstName} {selectedOrder.shippingAddress?.lastName}</span>
                  </div>
                  <div className="info-item">
                    <Phone size={14} />
                    <span>{selectedOrder.shippingAddress?.phone}</span>
                  </div>
                  <div className="info-item">
                    <MapPin size={14} />
                    <span>
                      {selectedOrder.shippingAddress?.street}, 
                      {selectedOrder.shippingAddress?.city}, 
                      {selectedOrder.shippingAddress?.state} - 
                      {selectedOrder.shippingAddress?.zip}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="modal-section">
                <h4>Order Summary</h4>
                <div className="summary-items">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>₹{selectedOrder.totalAmount?.toLocaleString()}</span>
                  </div>
                  <div className="summary-row">
                    <span>Shipping</span>
                    <span>Free</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total</span>
                    <span>₹{selectedOrder.totalAmount?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.orders-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.orders-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh:hover {
  border-color: #10b981;
  color: #10b981;
  transform: translateY(-2px);
}

.stats-mini-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

.stat-card-mini {
  background: white;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  transition: all 0.3s ease;
}

.stat-card-mini:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

.stat-icon-mini {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
}

.stat-title-mini {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 2px;
}

.stat-value-mini {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.filters-bar {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 320px;
}

.search-input-modern {
  width: 100%;
  padding: 10px 16px 10px 42px;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-input-modern:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.status-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.status-filter-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 40px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
}

.status-filter-btn.active {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.status-filter-btn:hover:not(.active) {
  border-color: #10b981;
  color: #10b981;
}

.btn-export-data {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #f1f5f9;
  border: none;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-export-data:hover {
  background: #e2e8f0;
}

.orders-table-container {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
}

.orders-table th {
  text-align: left;
  padding: 16px 20px;
  background: #f8fafc;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  border-bottom: 1px solid #e8ecef;
}

.orders-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #e8ecef;
  vertical-align: middle;
}

.orders-table tr:hover {
  background: #f8fafc;
}

.order-id {
  font-weight: 700;
  color: #1e293b;
  font-size: 14px;
}

.customer-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
  display: block;
  margin-bottom: 4px;
}

.customer-contact {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.products-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-item-preview {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-item-preview img {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
  background: #f1f5f9;
}

.product-name-preview {
  font-size: 12px;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
}

.product-name-preview small {
  color: #94a3b8;
  font-size: 10px;
}

.more-products {
  font-size: 11px;
  color: #10b981;
  margin-left: 40px;
}

.date-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.amount-cell {
  font-weight: 700;
  color: #10b981;
  font-size: 14px;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 40px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
}

.action-view,
.action-ship,
.action-deliver {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-view {
  background: #f1f5f9;
  border: none;
  color: #475569;
}

.action-view:hover {
  background: #e2e8f0;
}

.action-ship {
  background: #dbeafe;
  border: none;
  color: #3b82f6;
}

.action-ship:hover {
  background: #bfdbfe;
}

.action-deliver {
  background: #d1fae5;
  border: none;
  color: #10b981;
}

.action-deliver:hover {
  background: #a7f3d0;
}

.pagination-modern {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-btn-modern {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 40px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn-modern:hover:not(:disabled) {
  border-color: #10b981;
  color: #10b981;
}

.page-btn-modern:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers-modern {
  display: flex;
  gap: 6px;
}

.page-number-modern {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-number-modern.active {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.page-number-modern:hover:not(.active) {
  border-color: #10b981;
  color: #10b981;
}

.loading-container,
.empty-container {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 20px;
}

.empty-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 24px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  animation: modalSlideIn 0.3s ease;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e8ecef;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

.modal-subtitle {
  font-size: 12px;
  color: #94a3b8;
  margin: 4px 0 0;
}

.modal-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: #f1f5f9;
}

.modal-body {
  padding: 24px;
}

.modal-section {
  margin-bottom: 28px;
}

.modal-section h4 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 14px;
}

.status-update-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.status-update-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.status-update-btn.active {
  border-color: #10b981;
  background: #d1fae5;
  color: #10b981;
}

.order-items-list {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.order-item-detail {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border-bottom: 1px solid #e2e8f0;
}

.order-item-detail:last-child {
  border-bottom: none;
}

.order-item-detail img {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
}

.item-info {
  flex: 1;
}

.item-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
  margin-bottom: 4px;
}

.item-price {
  font-size: 12px;
  color: #64748b;
}

.item-subtotal {
  font-weight: 700;
  color: #10b981;
  font-size: 14px;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #475569;
}

.summary-items {
  background: #f8fafc;
  border-radius: 16px;
  padding: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  color: #475569;
}

.summary-row.total {
  border-top: 1px solid #e2e8f0;
  margin-top: 8px;
  padding-top: 12px;
  font-weight: 700;
  font-size: 16px;
  color: #1e293b;
}

@media (max-width: 768px) {
  .orders-page {
    padding: 16px;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    max-width: none;
  }

  .orders-table {
    font-size: 12px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .stats-mini-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}`}</style>
    </div>
  );
};

export default Orders;